import type { FeeSchedule } from "@netkeep/fee-types";
import { feeProfileSchema, isoDate, scheduleStatus, verificationStatus, z } from "@netkeep/validation";
import { REQUEST_TIMEOUT_MS } from "./config";

/** Metadata the app relies on, validated in addition to the engine profile. */
const scheduleMetaSchema = z.object({
  platform: z.object({ slug: z.string(), name: z.string() }),
  paymentMethod: z.object({ slug: z.string(), name: z.string() }),
  region: z.string(),
  transactionType: z.string(),
  version: z.number(),
  versionLabel: z.string(),
  status: scheduleStatus,
  verificationStatus: verificationStatus,
  effectiveFrom: isoDate,
  lastVerifiedAt: z.string().nullable(),
  source: z.object({ url: z.string(), title: z.string() }).nullable(),
});

const latestResponseSchema = z.object({
  asOf: isoDate,
  generatedAt: z.string(),
  items: z.array(z.unknown()),
});

export class ApiRequestError extends Error {}

/**
 * Fetches the schedules currently in effect. Each item is validated
 * independently; malformed items are dropped rather than trusted, so a bad
 * server response can never feed garbage into the fee engine.
 */
export async function fetchLatestSchedules(
  baseUrl: string,
  region: string,
): Promise<{ asOf: string; generatedAt: string; items: FeeSchedule[]; rejected: number }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`${baseUrl.replace(/\/+$/, "")}/api/v1/fee-schedules/latest?region=${region}`, {
      signal: controller.signal,
      headers: { accept: "application/json" },
    });
  } catch (err) {
    // Browsers report CORS and mixed-content (https page → http API) blocks as plain network errors.
    throw new ApiRequestError(
      controller.signal.aborted
        ? "The fee server did not respond in time."
        : `Could not reach the fee server (${String(err)}). Check the address, and that the server allows this site (CORS_ORIGINS).`,
    );
  } finally {
    clearTimeout(timer);
  }
  if (!response.ok) throw new ApiRequestError(`Fee server returned HTTP ${response.status}.`);

  const parsed = latestResponseSchema.safeParse(await response.json());
  if (!parsed.success) throw new ApiRequestError("Fee server returned an unexpected response.");

  const items: FeeSchedule[] = [];
  let rejected = 0;
  for (const raw of parsed.data.items) {
    if (feeProfileSchema.safeParse(raw).success && scheduleMetaSchema.safeParse(raw).success) {
      items.push(raw as FeeSchedule);
    } else {
      rejected++;
    }
  }
  if (items.length === 0) throw new ApiRequestError("Fee server returned no usable fee schedules.");
  return { asOf: parsed.data.asOf, generatedAt: parsed.data.generatedAt, items, rejected };
}

export async function checkHealth(baseUrl: string): Promise<boolean> {
  try {
    const res = await fetch(`${baseUrl.replace(/\/+$/, "")}/api/v1/health`);
    return res.ok;
  } catch {
    return false;
  }
}
